import { Attraction } from "typings/microserviceModels/destination-attractions-flex-module";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { UitkCard, UitkCardLink, UitkCardMedia, UitkCardTextSegment } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { MediaAttribution } from "components/utility/MediaAttributionUtil";
import { EntityType, HeartButton } from "components/shared/HeartButton/HeartButton";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import * as React from "react";
import { AggregateRating } from "components/shared/Rating/AggregateRating";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import LazyLoad from "react-lazyload";
import { LazyLoaderPlaceHolder } from "components/utility/LazyLoaderPlaceholderWrapper";

export const AttractionCard = ({
  attraction,
  imageUrl,
  position,
  context,
  uitkCardClassNames,
  isCountry,
  isActivity,
  attractionsKeyHelper
}: {
  attraction: Attraction;
  imageUrl: string;
  position: number;
  context: ExtendedContextStore;
  uitkCardClassNames: string;
  isCountry: boolean;
  isActivity: boolean;
  attractionsKeyHelper: ItemKeyHelper;
}) => {
  const { name, parentName, media, link } = attraction;
  const description = attraction.description.replace(/\n/g, " ");
  const price = attraction.price;

  // In the microservice country response...
  //  - source is mis-assigned to origin
  //  - mediaUrlOriginal is absent
  if (isCountry) {
    media.source = media.origin;
    media.mediaUrlOriginal = media.mediaUrl;
  }
  const attribution = MediaAttribution(media, name);

  const totalRatings =
    attraction.aggregateReviewsAndRatings && attraction.aggregateReviewsAndRatings.totalRatings;
  const averageRating =
    attraction.aggregateReviewsAndRatings && attraction.aggregateReviewsAndRatings.averageRating;

  return (
    <UitkCard className={uitkCardClassNames}>
      <UitkCardLink>
        <TrackedLink
          moduleName="TG.LP.Dest.Attractions"
          href={link}
          rfrr={isActivity ? "Activity.Card.Link" : "Poi.Card.Link"}
        >
          {name}
        </TrackedLink>
      </UitkCardLink>
      <UitkCardMedia className="attractionCard__uitk-card__media-card">
        <UitkFigure ratio={UitkFigureAspectRatioType.R16_9}>
          <LazyLoad placeholder={<LazyLoaderPlaceHolder name={name} />} offset={250}>
            <UitkImage alt={attribution && attribution.attributionAltText} src={imageUrl} />
          </LazyLoad>
          {attribution && attribution.attributionText}
          <HeartButton
            moduleName={"TG.LP.Dest.Attractions"}
            heartableEntity={{
              id: attraction.id,
              name: attraction.name,
              entityType: EntityType.POINT_OF_INTEREST,
              payload: {}
            }}
          />
        </UitkFigure>
      </UitkCardMedia>
      <div className="uitk-card-content uitk-cell">
        {isActivity ? (
          <ActivityCardTitleSegment name={name} price={price} />
        ) : (
          <PoiCardTitleSegment
            name={name}
            parentName={parentName}
            isCountry={isCountry}
            averageRating={averageRating}
            totalRatings={totalRatings}
            key={attractionsKeyHelper.next()}
            context={context}
          />
        )}
        <UitkCardTextSegment>
          <div
            className="uitk-type-300"
            dangerouslySetInnerHTML={{ __html: `<p>${description}</p>` }}
          />
        </UitkCardTextSegment>
      </div>
    </UitkCard>
  );
};

export const PoiCardTitleSegment = ({
  name,
  parentName,
  isCountry,
  averageRating,
  totalRatings,
  context
}: {
  name: string;
  parentName: string;
  isCountry: boolean;
  averageRating?: number;
  totalRatings?: number;
  context: ExtendedContextStore;
}) => {
  return (
    <UitkCardTextSegment className="uitk-card-separator-bottom">
      <div className="uitk-grid">
        <div className="uitk-cell all-cell-fill">
          <h4 className="uitk-type-heading-400">{name}</h4>
          {isCountry && parentName && <p className="uitk-type-200 poiParentName">{parentName}</p>}
        </div>
        <div className="uitk-cell all-cell-shrink all-cell-align-middle">
          <AggregateRating rating={averageRating} reviewCount={totalRatings} context={context} />
        </div>
      </div>
    </UitkCardTextSegment>
  );
};

export const ActivityCardTitleSegment = ({ name, price }: { name: string; price: string }) => {
  return (
    <UitkCardTextSegment className="uitk-card-separator-bottom">
      <div className="uitk-grid all-x-gutter-two">
        <div className="uitk-cell all-cell-fill">
          <h4 className="uitk-type-heading-400">{name}</h4>
        </div>
        {price && (
          <div className="uitk-cell all-cell-shrink all-l-margin-two">
            <p className="uitk-type-600 uitk-type-bold">{price}</p>
          </div>
        )}
      </div>
    </UitkCardTextSegment>
  );
};
